/**
 * SEO component that queries for data with
 *  Gatsby's useStaticQuery React hook
 *
 * See: https://www.gatsbyjs.org/docs/use-static-query/
 */

import React from 'react';
import {Helmet} from 'react-helmet';
import {useStaticQuery, graphql} from 'gatsby';

type Meta =
  | {name: string; content: any; property?: undefined}
  | {property: string; content: any; name?: undefined};

type Props = {
  description?: string;
  lang?: string;
  meta?: Meta[];
  title: string;
  ogImageUrl?: string;
  ogImageSize?: {width: number; height: number};
};

function SEO({
  description = '',
  lang = 'en',
  meta = [],
  title,
  ogImageUrl,
  ogImageSize,
}: Props) {
  const {site, contentfulHeaderAndFooter} = useStaticQuery(
    graphql`
      query {
        site {
          siteMetadata {
            title
            description
            author
            siteUrl
          }
        }
        contentfulHeaderAndFooter {
          ogimage {
            file {
              url
            }
          }
        }
      }
    `
  );

  const metaDescription = description || site.siteMetadata.description;

  const getOgImage = () => {
    if (ogImageUrl) {
      if (ogImageUrl.startsWith('/')) {
        return `${site.siteMetadata.siteUrl}${ogImageUrl}`;
      }
      return `${site.siteMetadata.siteUrl}/${ogImageUrl}`;
    }

    return `https:${contentfulHeaderAndFooter.ogimage.file.url}`;
  };

  const ogImage = getOgImage();

  return (
    <Helmet
      htmlAttributes={{
        lang,
      }}
      title={title}
      titleTemplate={`${site.siteMetadata.title} - %s`}
      meta={[
        {
          name: 'description',
          content: metaDescription,
        },
        {
          property: 'og:title',
          content: `${site.siteMetadata.title} | ${title}`,
        },
        {
          property: 'og:description',
          content: metaDescription,
        },
        {
          property: 'og:type',
          content: 'website',
        },
        {
          property: 'og:image',
          content: ogImage,
        },
        {
          property: 'og:image:width',
          content: ogImageSize?.width ?? 1200,
        },
        {
          property: 'og:image:height',
          content: ogImageSize?.height ?? 527,
        },
        {
          property: 'og:site_name',
          content: site.siteMetadata.author,
        },
        {
          name: 'twitter:card',
          content: 'summary_large_image',
        },
        {
          name: 'twitter:creator',
          content: site.siteMetadata.author,
        },
        {
          name: 'twitter:title',
          content: title,
        },
        {
          name: 'twitter:description',
          content: metaDescription,
        },
        {
          name: 'viewport',
          content:
            'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=0',
        },
      ].concat(meta)}
    />
  );
}

export default SEO;
